import { useTranslation } from 'react-i18next';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { IVerticalDropsResults } from '../../../api/IApiResponse';
import './StaticResults.scss';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { getVerticalDropsType } from '../../../../utils/utils';
import { selectProject } from '../../../selector/conversion';
import { EVerticalDropsTypes } from '../../../enum/general';

interface IStaticVerticalProps {}

const StaticVertical = (props: IStaticVerticalProps) => {
    const { t } = useTranslation();
    const { selectResults } = getActionByProjectType();

    const results: IInitialState<IVerticalDropsResults> = useAppSelector(selectResults as any);
    const projectInfo = useAppSelector(selectProjectInfo);
    const project = useAppSelector(selectProject);
    const showUpliftPressure =
        getVerticalDropsType(project.data?.stillingBasin) === EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth ||
        getVerticalDropsType(project.data?.stillingBasin) === EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth;
    return (
        <div className='static-results d-flex flex-column align-items-center gap-3'>
            <div className='d-flex w-100'>
                <div className='d-flex flex-column justify-content-center align-items-center gap-3 h-85 w-100 width-table-80'>
                    <table className='tableStabilityChecks'>
                        <thead>
                            <tr>
                                <th>{t('output_static_vertical_stability_checks')}</th>
                                <th>{t('output_symbol')}</th>
                                <th>{t('output_value')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_safety_against_sliding')}</td>
                                <td>
                                    <SmallLetter normal='FS' small='sl' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.stabilityChecksOutput.safetyFactorAgainstSliding)}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_safety_against_overturning')}</td>
                                <td>
                                    <SmallLetter normal='FS' small='ov' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.stabilityChecksOutput.safetyFactorAgainstOverturning)}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_safety_against_seepage')}</td>
                                <td>
                                    <SmallLetter normal='FS' small='see' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.stabilityChecksOutput.safetyFactorAgainstSiphoning)}</td>
                            </tr>
                            {showUpliftPressure && (
                                <tr>
                                    <td>{t('output_static_vertical_safety_against_uplift_pressure')}</td>
                                    <td>
                                        <SmallLetter normal='FS' small='up' />
                                    </td>
                                    <td>{roundToTwo(results.data?.staticResult.stabilityChecksOutput.safetyCoefficientAgainstUpliftPressure)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <div className='w-80'>{t('note_for_vertical_results')}</div>

                    <table className='tableSoilThrust'>
                        <thead>
                            <tr>
                                <th>{t('output_static_vertical_soil_thrust')}</th>
                                <th>{t('output_symbol')}</th>
                                <th>{t('output_value')}</th>
                                <th>{t('output_unit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_active_thrust')}</td>
                                <td>
                                    <SmallLetter normal='K' small='a' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.activeThrustCoefficient)}</td>
                                <td>
                                    <div className='d-flex justify-content-center'>-</div>
                                </td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_total_height')}</td>
                                <td>
                                    <SmallLetter normal='h' small='w' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.totalHeightOfTheStructure!)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_soil_thrust_upstream')}</td>
                                <td>
                                    <SmallLetter normal='P' small='am' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.soilThrustOfTheUpstreamBackfill)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_horizontal_upstream')}</td>
                                <td>
                                    <SmallLetter normal='P' small='amh' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.horizontalComponentOfUpstreamThrust)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_lever_arm_Pamh')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Pamh' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.leverArmOfPamh)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_moment_horizontal_upstream')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Pamh' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.momentOfHorizontalUpstreamThrust)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_vertical_upstream')}</td>
                                <td>
                                    <SmallLetter normal='P' small='amv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.verticalComponentOfUpstreamThrust)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_lever_arm_Pamv')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Pamv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.leverArmOfPamv)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_moment_vertical_upstream')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Pamv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.momentOfVerticalUpstreamThrust)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_downstream_soil')}</td>
                                <td>
                                    <SmallLetter normal='P' small='av' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.downstreamSoilActiveThrust)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_lever_arm_Pav')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Pav' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.leverArmOfPav)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_moment_downstream')}</td>
                                <td>
                                    <SmallLetter normal='M' small='Pav' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.soilThrustOutput.momentOfDownstreamThrust)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                        </tbody>
                    </table>
                    <table className='tableWeights'>
                        <thead>
                            <tr>
                                <th colSpan={2}>{t('output_static_vertical_weights')}</th>
                                <th>{t('output_symbol')}</th>
                                <th>{t('output_value')}</th>
                                <th>{t('output_unit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='bg-white'>
                                <td rowSpan={3} className='bg-td-weight'>
                                    {t('output_static_vertical_gabion_weir')}
                                </td>
                                <td>{t('output_static_vertical_weir_unit_weight')}</td>
                                <td>WG</td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.weirUnitWeight)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_lever_arm_weir_weight')}</td>
                                <td>
                                    <SmallLetter normal='b' small='wg' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.leverArmOfTheWeirWeight)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_moment_weir_weight')}</td>
                                <td>
                                    <SmallLetter normal='M' small='wg' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.momentOfTheWeirWeight)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td rowSpan={3} className='bg-td-weight'>
                                    {t('output_static_vertical_soil')}
                                </td>
                                <td>{t('output_static_vertical_soil_unit_weight')}</td>
                                <td>WS</td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.soilUnitWeight)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_lever_arm_soil_weight')}</td>
                                <td>
                                    <SmallLetter normal='b' small='ws' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.leverArmOfTheSoilWeight)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_moment_soil_weight')}</td>
                                <td>
                                    <SmallLetter normal='M' small='ws' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.momentOfTheSoilWeight)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td rowSpan={3} className='bg-td-weight'>
                                    {t('output_static_vertical_water')}
                                </td>
                                <td>{t('output_static_vertical_water_unit_weight')}</td>
                                <td>WW</td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.waterUnitWeight)}</td>
                                <td>{conversionLabel.kN_lbf[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_lever_arm_water_weight')}</td>
                                <td>
                                    <SmallLetter normal='b' small='ww' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.leverArmOfTheWaterWeight)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_moment_water_weight')}</td>
                                <td>
                                    <SmallLetter normal='M' small='ww' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.weightsOutput.momentOfTheWaterWeight)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className='d-flex flex-column justify-content-center align-items-center gap-3 h-85 w-100 width-table-80'>
                    <table className='tableResultingForcesAndMoments'>
                        <thead>
                            <tr>
                                <th>{t('output_static_vertical_resulting_forces_moments')}</th>
                                <th>{t('output_symbol')}</th>
                                <th>{t('output_value')}</th>
                                <th>{t('output_unit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_normal_force')}</td>
                                <td>N</td>
                                <td>{roundToTwo(results.data?.staticResult.resultingForcesAndMomentsOutput.normalForceOnTheFoundation)}</td>
                                <td>{conversionLabel.kN_lbf[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_shear_stress')}</td>
                                <td>T</td>
                                <td>{roundToTwo(results.data?.staticResult.resultingForcesAndMomentsOutput.shearStressOnTheFoundation)}</td>
                                <td>{conversionLabel.kN_lbf[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_sum_stabilizing')}</td>
                                <td>
                                    <SmallLetter normal='M' small='stab' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.resultingForcesAndMomentsOutput.sumOfStabilizingMoments)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_sum_destibilizing')}</td>
                                <td>
                                    <SmallLetter normal='M' small='rib' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.resultingForcesAndMomentsOutput.sumOfDestibilizingMoments)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                        </tbody>
                    </table>
                    <table className='tableHydrostaticPressure'>
                        <thead>
                            <tr>
                                <th>{t('output_static_vertical_hydrostatic_pressure')}</th>
                                <th>{t('output_symbol')}</th>
                                <th>{t('output_value')}</th>
                                <th>{t('output_unit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_upstream_hydrostatic_pressure')}</td>
                                <td>
                                    <SmallLetter normal='S' small='wm' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.hydrostaticPressureOutput.upstreamHydrostaticPressure)}</td>
                                <td>{conversionLabel.kN_lbf[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_lever_arm_upstream_hydrostatic_pressure')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Swm' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.hydrostaticPressureOutput.leverArmOfUpstreamHydrostaticPressure)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_moment_upstream_hydrostatic_pressure')}</td>
                                <td>
                                    <SmallLetter normal='M' small='Swm' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.hydrostaticPressureOutput.momentOfUpstreamHydrostaticPressure)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_downstream_hydrostatic_pressure')}</td>
                                <td>
                                    <SmallLetter normal='S' small='wv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.hydrostaticPressureOutput.downstreamHydrostaticPressure)}</td>
                                <td>{conversionLabel.kN_lbf[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_lever_arm_downstream_hydrostatic_pressure')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Swv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.hydrostaticPressureOutput.leverArmOfDownstreamHydrostaticPressure)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_moment_downstream_hydrostatic_pressure')}</td>
                                <td>
                                    <SmallLetter normal='M' small='Swv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.hydrostaticPressureOutput.momentOfDownstreamHydrostaticPressure)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                        </tbody>
                    </table>
                    <table className='tableUpliftPressure'>
                        <thead>
                            <tr>
                                <th>{t('output_static_vertical_uplift_pressure')}</th>
                                <th>{t('output_symbol')}</th>
                                <th>{t('output_value')}</th>
                                <th>{t('output_unit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_filtration_length')}</td>
                                <td>
                                    <SmallLetter normal='L' small='f' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.upliftPressureOutput.filtrationLength)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_upstream_uplift_pressure')}</td>
                                <td>
                                    <SmallLetter normal='S' small='pwm' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.upliftPressureOutput.upstreamUpliftPressure)}</td>
                                <td>{conversionLabel.kNm_lbfft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_downstream_uplift_pressure')}</td>
                                <td>
                                    <SmallLetter normal='S' small='pwv' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.upliftPressureOutput.downstreamUpliftPressure)}</td>
                                <td>{conversionLabel.kNm_lbfft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_hydraulic_uplift')}</td>
                                <td>
                                    <SmallLetter normal='V' small='w' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.upliftPressureOutput.hydraulicUplift)}</td>
                                <td>{conversionLabel.kN_lbf[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr className='bg-white'>
                                <td>{t('output_static_vertical_lever_arm_hydraulic_uplift')}</td>
                                <td>
                                    <SmallLetter normal='b' small='Vw' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.upliftPressureOutput.leverArmOfHydraulicUplift)}</td>
                                <td>{conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                            <tr>
                                <td>{t('output_static_vertical_moment_hydraulic_uplift')}</td>
                                <td>
                                    <SmallLetter normal='M' small='Vw' />
                                </td>
                                <td>{roundToTwo(results.data?.staticResult.upliftPressureOutput.momentOfTheHydraulicUplift)}</td>
                                <td>{conversionLabel.kN_m_lbf_ft[projectInfo.data?.unitOfMeasure as number]}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StaticVertical;
